/**
 * Put a finished questionnaire extraction onto the open form, deterministically.
 *
 * The chat-agent stamps a `StageViewChanges` payload on the run the moment the
 * extraction completes; this dispatches it straight to the surface broker. No
 * model turn, no A2UI card, no POST — the extractor already keys its answers by
 * form field id, so routing them through an LLM would add nothing but a chance
 * to get them wrong.
 *
 * Nothing here saves. The broker's `stage` capability turns the payload into
 * drafts on the fields they belong to, and the user accepts or rejects each one
 * on the form.
 *
 * Fires once per change-set. The payload's `changeSetId` is derived from the
 * run, so a card remounting (a scroll, a rehydrate, a reopened conversation)
 * dispatches nothing the second time — and if it does slip past this latch, the
 * capability itself refuses with `already-applied`.
 */
import { useEffect, useRef, useState } from 'react';
import {
  dispatchViewSurfaceAction,
  type ViewSurfaceCapabilityResult,
} from '@cowbell/surface-broker';
import { isRetryableStageRefusal, type FormDraftResult } from '@cowbell/ui';
import { riskDataViewKey } from './buildRiskDataViewSnapshot';

/**
 * What the card renders — literally the card's own result type, so the two
 * halves of this flow cannot drift apart.
 */
export type StageOutcome = FormDraftResult;

/** The broker request the run carries, plus the labels the card shows. */
export interface StagePayloadEnvelope {
  readonly payload: Record<string, unknown>;
  readonly formName?: string;
  readonly answeredCount?: number;
}

export interface UseStageExtractedAnswers {
  readonly outcome: StageOutcome | null;
  readonly pending: boolean;
  /** Re-dispatch after a refusal the user can clear by navigating. */
  readonly retry: () => void;
}

/**
 * The `viewKey` of the questionnaire mount these answers belong to, or `''`
 * when the payload does not identify one.
 *
 * `formId` is optional on the wire, so an empty string is a real outcome and
 * not an error: it restores the old fall-through, which is worse than routing
 * but better than refusing a payload the capability itself would have accepted.
 */
function stageTargetViewKey(payload: Record<string, unknown> | undefined) {
  const accountId = payload?.entityId;
  const formId = payload?.formId;
  if (typeof accountId !== 'string' || typeof formId !== 'string') {
    return '';
  }
  return riskDataViewKey(accountId, formId);
}

export function useStageExtractedAnswers(
  envelope: StagePayloadEnvelope | null | undefined
): UseStageExtractedAnswers {
  const [outcome, setOutcome] = useState<StageOutcome | null>(null);
  const [pending, setPending] = useState(false);
  // The dispatch itself, per change-set — not merely the fact that one
  // happened. A ref, so it is readable synchronously inside the effect: a
  // double render must not dispatch twice before the first result lands.
  //
  // The promise is kept rather than a boolean because the effect re-runs for
  // reasons that have nothing to do with the request: Strict Mode's double
  // invoke, and a poll handing down a fresh `envelope` object. A re-run that
  // found only a boolean would decline to dispatch and have nothing to wait
  // on, so the answer to a request that is already on its way would land on a
  // cleaned-up closure and be discarded — no outcome, and `pending` stuck on
  // forever. Re-attaching to the promise reports it however often that
  // happens, and an entry that is already settled simply reports immediately.
  const dispatches = useRef<Map<string, Promise<ViewSurfaceCapabilityResult>>>(
    new Map()
  );
  const [attempt, setAttempt] = useState(0);

  const changeSetId =
    typeof envelope?.payload?.changeSetId === 'string'
      ? envelope.payload.changeSetId
      : '';

  // The mount this payload is for, derived rather than carried. The payload was
  // built server-side and has never seen a browser mount, but `viewKey` is a
  // pure function of the two identifiers it does carry, so it can name one.
  //
  // This matters because the broker falls back to whatever surface is active
  // when given a blank key. Risk Data and the prime-x underwriter are sibling
  // tabs of one workspace and both claim `StageViewChanges`, so a blank key
  // sends an extraction into the underwriter, which refuses it as
  // `unknown-command` — a refusal the card cannot offer a retry for.
  const targetViewKey = stageTargetViewKey(envelope?.payload);

  // Read through a ref, and keyed on the change-set instead. The envelope is a
  // fresh object on every poll, so an effect that depended on it would re-run
  // on a schedule — and each re-run sets `pending` before it can discover it
  // has nothing to do, which leaves the card flickering between states forever.
  // What identifies this request is its change-set id, not the object carrying
  // it.
  const envelopeRef = useRef(envelope);
  envelopeRef.current = envelope;

  useEffect(() => {
    const current = envelopeRef.current;
    if (!current || !changeSetId) {
      return;
    }

    let live = true;
    setPending(true);

    const started =
      dispatches.current.get(changeSetId) ??
      dispatchViewSurfaceAction({
        name: 'StageViewChanges',
        viewKey: targetViewKey,
        payload: current.payload,
      });
    dispatches.current.set(changeSetId, started);

    started
      .then((result) => {
        if (!live) {
          return;
        }
        if (result.ok) {
          const data = (result.data ?? {}) as Partial<StageOutcome>;
          setOutcome({
            status: 'staged',
            formName: data.formName ?? current.formName,
            stagedCount: data.stagedCount,
            unchangedCount: data.unchangedCount,
            unresolved: data.unresolved,
            // On the result, not in `data` — the capability puts its prose
            // sentence there and the counts in the payload.
            message: result.message,
          });
          return;
        }
        // `already-applied` is usually a SUCCESS being reported late. The
        // latch is a component ref, so a remount — a scroll, a rehydrate, a
        // reopened conversation — dispatches the same change-set again and the
        // capability correctly refuses it. Saying "those answers could not be
        // put on the form" directly above them is simply false. No count,
        // because this dispatch did not stage anything.
        //
        // Gated on `draftsOnForm`: the id also survives a schema change that
        // dropped the proposals, and pointing the user at drafts that are no
        // longer there is the same lie in the other direction.
        const data = (result.data ?? {}) as { draftsOnForm?: boolean };
        if (result.reason === 'already-applied' && data.draftsOnForm) {
          setOutcome({ status: 'staged', formName: current.formName });
          return;
        }
        // A refusal the user can clear becomes retryable, so drop the latch —
        // otherwise "open the questionnaire and try again" is a button that
        // does nothing.
        if (isRetryableStageRefusal(result.reason)) {
          dispatches.current.delete(changeSetId);
        }
        setOutcome({
          status: 'rejected',
          reason: result.reason,
          formName: current.formName,
          message: result.message,
        });
      })
      .catch((err: unknown) => {
        if (!live) {
          return;
        }
        // Left retryable: a thrown dispatch says nothing about whether the
        // answers are stageable, only that this attempt broke. The reason is
        // stamped rather than left blank, because the card decides whether to
        // offer "Try again" from it — dropping the latch without one made the
        // catch path retryable in the hook and unretryable on screen.
        dispatches.current.delete(changeSetId);
        setOutcome({
          status: 'rejected',
          reason: 'dispatch-failed',
          formName: current.formName,
          message:
            err instanceof Error
              ? err.message
              : 'Those answers could not be put on the form.',
        });
      })
      .finally(() => {
        if (live) {
          setPending(false);
        }
      });

    return () => {
      live = false;
    };
    // `attempt` is the retry trigger; the payload is stable per change-set.
  }, [changeSetId, targetViewKey, attempt]);

  return {
    outcome,
    pending,
    retry: () => setAttempt((n) => n + 1),
  };
}
